<template>
  <v-row no-gutters>
    <v-col cols="12">
      <v-row no-gutters class="draggable-list-col mb-4" style="gap: 1rem">
        <v-col>
          <v-spacer></v-spacer>
        </v-col>
        <v-col cols="auto">
          <base-button
            @click="dialogAddAction('Report')"
            translation-path="admin.customReports.addReportButton"
            class="mr-0"
          />
        </v-col>
        <v-col cols="auto">
          <base-button
            @click="dialogAddAction('Group')"
            cancel
            translation-path="admin.customReports.addGroupButton"
            class="mr-0"
          />
        </v-col>
      </v-row>
      <draggable-list
        v-bind="{ ...$props, ...$attrs }"
        :value.sync="reportsTree"
        disable-group-adding
        :child-items-key="childItemsKey"
        item-title-key="name"
        hideable-groups
        @elementAdded="onAdd"
        @elementUpdated="onUpdate"
        :groups-dots-items="getDotsItems('group')"
        :elements-dots-items="getDotsItems('item')"
        @delete="dialogAction('Delete', $event)"
        @edit="dialogAction('Edit', $event)"
        @changeName="dialogAction('ChangeName', $event)"
      />
    </v-col>
    <zdm-reports-tab-custom-dialog
      v-if="isAddReportDialogLoaded"
      :is-visible.sync="isAddReportDialogVisible"
      :report-data.sync="currentReport"
      :report-groups="reportsGroups"
    />
    <main-input-dialog
      v-if="isAddGroupDialogLoaded"
      :is-visible.sync="isAddGroupDialogVisible"
      :inputs="addGroupInputs"
      :title-translation="$i18n.t(addGroupInputs[0].isNew ? 'dialog.addGroup' : 'dialog.editGroup')"
      :agree-text="addGroupInputs[0].isNew ? 'dialog.agreeAdd' : 'dialog.agreeEdit'"
      :loading="isAddGroupLoading"
      @agree="addGroup"
    />
    <main-dialog
      :v-if="isDeleteDialogLoaded"
      :is-visible.sync="isDeleteDialogVisible"
      :loading="isDeleteDialogLoading"
      :title="deleteInfo.type ? $i18n.t(`admin.customReports.delete${deleteInfo.type}Title`) : ''"
      :body="
        deleteInfo.type
          ? $i18n.t(`admin.customReports.delete${deleteInfo.type}Body`, { name: deleteInfo.name || '' })
          : ''
      "
      @agree="handleDelete"
      agree-text="dialog.agreeDelete"
    />
  </v-row>
</template>

<script>
import DraggableList from '@/components/DraggableList';

import { get, call } from 'vuex-pathify';

export default {
  name: 'CustomReportsListPanel',
  components: {
    DraggableList,
    MainInputDialog: () => import('@/components/MainInputDialog'),
    ZdmReportsTabCustomDialog: () => import('@/components/ZDMReportsTabCustomDialog'),
    MainDialog: () => import('@/components/MainDialog'),
  },
  data: () => ({
    isAddReportDialogLoaded: false,
    isAddReportDialogVisible: false,
    isAddGroupDialogLoaded: false,
    isAddGroupDialogVisible: false,
    isAddGroupLoading: false,
    deleteInfo: {},
    isDeleteDialogLoaded: false,
    isDeleteDialogVisible: false,
    isDeleteDialogLoading: false,
    currentReport: null,
    childItemsKey: 'reports',
  }),
  computed: {
    reportsGroups: get('customReports/reportsGroups'),
    reports: get('customReports/reports'),
    reportsTree() {
      const hiddenGroups = JSON.parse(localStorage.getItem('custom-reports-hidden-groups') || '{}');
      return this.reportsGroups.map(group => {
        return {
          ...group,
          groupName: 'custom-reports',
          isHidden: hiddenGroups[group.id] || false,
          reports:
            this.reports
              .find(report => report.group_id === group.id)
              ?.reports?.map(report => {
                return {
                  ...report,
                  tooltip: report.name,
                  group_id: group.id,
                };
              }) || [],
        };
      });
    },
    addGroupInputs() {
      return [
        {
          name: 'groupName',
          isNew: true,
          editId: null,
          order: null,
          translationPath: 'dialog.groupName',
          dataType: { name: 'text' },
          rules: [v => !!v || v == '0' || 'rules.required'],
          cols: '12',
          align: 'baseline',
        },
      ];
    },
  },
  methods: {
    ...call('customReports/*CustomRepor*'),
    async onAdd(e) {
      const groupId = this.reportsTree[e.to.parentNode.id].id;
      const newOrder = e.newIndex;
      let itemProperties = e.item._underlying_vm_;
      try {
        await this.editCustomReport({
          id: itemProperties.id,
          data: {
            group_id: groupId,
            group_order: newOrder,
          },
        });
      } catch {
        //
      }
    },
    async onUpdate(e) {
      let itemProperties = e.item._underlying_vm_;
      const newOrder = e.newIndex;
      try {
        if (itemProperties.reports) {
          await this.editCustomReportGroup({
            id: itemProperties.id,
            data: {
              name: itemProperties.name,
              order: newOrder,
            },
          });
        } else {
          await this.editCustomReport({
            id: itemProperties.id,
            data: {
              group_order: newOrder,
            },
          });
        }
      } catch {
        //
      }
    },
    getDotsItems(type) {
      return [{ name: type === 'group' ? 'changeName' : 'edit' }, { name: 'delete' }];
    },
    dialogAddAction(type) {
      if (type === 'Group')
        [
          ['isNew', true],
          ['order', 0],
          ['value', null],
        ].forEach(([key, value]) => this.$set(this.addGroupInputs[0], key, value));
      this.currentReport = null;
      this[`isAdd${type}DialogLoaded`] = true;
      this[`isAdd${type}DialogVisible`] = true;
    },
    async handleDelete() {
      this.isDeleteDialogLoading = true;
      try {
        this.deleteInfo.type === 'Group'
          ? await this.deleteCustomReportGroup(this.deleteInfo.id)
          : await this.deleteCustomReport(this.deleteInfo.id);
      } finally {
        this.isDeleteDialogLoading = false;
        this.isDeleteDialogVisible = false;
      }
    },
    async addGroup() {
      this.isAddGroupLoading = true;
      try {
        this.addGroupInputs[0].isNew
          ? await this.addCustomReportGroup({
              name: this.addGroupInputs[0].value,
              order: 0,
            })
          : await this.editCustomReportGroup({
              id: this.addGroupInputs[0].editId,
              data: {
                order: this.addGroupInputs[0].order,
                name: this.addGroupInputs[0].value,
              },
            });
        this.isAddGroupLoading = false;
      } catch {
        this.isAddGroupLoading = false;
      } finally {
        this.isAddGroupDialogVisible = false;
        this.isAddGroupDialogLoaded = false;
      }
    },
    dialogAction(type, ev) {
      if (type === 'Delete') {
        this.isDeleteDialogLoaded = true;
        this.isDeleteDialogVisible = true;
        this.deleteInfo = {
          type: ev.reports ? 'Group' : 'Report',
          name: ev.name,
          id: ev.id,
        };
      } else if (type === 'ChangeName') {
        this.isAddGroupDialogLoaded = true;
        this.isAddGroupDialogVisible = true;
        [
          ['editId', ev.id],
          ['order', ev.order],
          ['isNew', false],
          ['value', ev.name],
        ].forEach(([key, value]) => {
          this.$set(this.addGroupInputs[0], key, value);
        });
      } else if (type === 'Edit') {
        this.currentReport = ev;
        this.isAddReportDialogLoaded = true;
        this.isAddReportDialogVisible = true;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.draggable-list-col {
  @include customColSize(48);
}
</style>
